import { maxBy, startCase } from 'es-toolkit';
import React from 'react';
import { connect } from 'react-redux';

import { formatForce, formatMoment } from '@paragon/client-tools';
import { Fixity, formatBoundaryType, formatLumberGrade, formatLumberSpeciesLong } from '@paragon/contract-library';
import { connectorDisplayName } from '@paragon/layout-models';
import { createNominalLumberDimensionFormatter, getPlateSizeString } from '@paragon/material-lists';
import { findLine, getAllMembers } from '@paragon/relative-geometry';
import { calculateMemberOverallLength } from '@paragon/three-shared';
import { humanCase, naturalSort, notNullish } from '@paragon/utilities';

import { RootState } from '../../../store';
import { getObjectCheckFailureSummaryString } from '../../../store/selectors/analysisSetsSelectors';
import { getHangerModelsAssociatedWithBearing } from '../../../store/selectors/bearingHangerSelectors';
import { getActiveAnalysisSet } from '../../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import { getDistanceFormatterSelector } from '../../../store/selectors/getDistanceFormatterSelector';
import { getPlateTypeFormatter } from '../../../store/selectors/platePairFormatters';
import { lookupHangerMeshKey } from '../../../three/layout/HangerVisual';
import { getAnalogPanelLength } from '../../interface/subpanels/properties/AnalogPanelProperties';
import { formatBearingReaction } from '../../interface/subpanels/properties/MaximumBearingReactionsTable';
import { TooltipTextContent } from './common';

type Props = ReturnType<typeof mapStateToProps>;

class DesignModeTooltip extends React.Component<Props> {
  getHoveredItemText = () => {
    const {
      componentDesign,
      analysisSet,
      distanceFormatter,
      formatNominalLumberDimensions,
      hoveredItem,
      plateTypeFormatter,
    } = this.props;

    if (!componentDesign || !hoveredItem) return undefined;

    const { platePairs, bearings } = componentDesign.component;
    const members = getAllMembers(componentDesign.component);

    const { guid, type } = hoveredItem;

    const objectCheckFailureSummaryString = getObjectCheckFailureSummaryString(guid, analysisSet);

    switch (type) {
      case 'member':
      case 'brace':
        const member = members.find((possibleMember) => possibleMember.guid === guid);
        if (!member) {
          console.error('Could not find member');
          return undefined;
        }
        const memberLocked = componentDesign.membersLocked || componentDesign.lockedMembers.includes(member.guid);
        const memberLockStatus = memberLocked ? 'Locked' : 'Unlocked';

        const lumberDimensions = member && formatNominalLumberDimensions(member.lumber);

        const length = calculateMemberOverallLength(member, componentDesign.component);

        // Even if the user has hidden member labels for the scene, they may want to see the member label in the tooltip
        const memberLabelOptions =
          member.memberType === 'Web'
            ? this.props.memberLabelOptions.webLabelContent
            : this.props.memberLabelOptions.nonWebLabelContent;
        const memberDisplayName = memberLabelOptions === 'AssemblyName' ? member.assemblyName : member.name;

        return `${memberDisplayName}
          ${lumberDimensions}
          ${length ? distanceFormatter(length) : 'unknown length'}
          ${formatLumberSpeciesLong(member.lumber.species, member.lumber.tangibleSpecies)}
          ${formatLumberGrade(member.lumber.grade, member.lumber.tangibleGrades)}
          ${member.lumber.treatmentType !== 'Untreated' ? `Treatment: ${humanCase(member.lumber.treatmentType)}` : ''}
          ${objectCheckFailureSummaryString}
          ${memberLockStatus}`;
      case 'platePair': {
        const platePair = platePairs.find((possiblePlatePair) => possiblePlatePair.guid === guid);
        if (!platePair) {
          console.error('Could not find plate pair');
          return undefined;
        }
        const platePairLocked =
          componentDesign.platingLocked || componentDesign.lockedPlatePairs.includes(platePair.guid);
        const platePairLockStatus = platePairLocked ? 'Locked' : 'Unlocked';
        return (
          platePair &&
          `${platePair.name}
          ${plateTypeFormatter(platePair.plateType, platePair.tangiblePlateTypesForComboPlateType)}
          ${getPlateSizeString(platePair)}
          ${platePair.platePlacement === 'TopBottom' ? '\nTop-Bottom' : ''}
          ${objectCheckFailureSummaryString}
          ${platePairLockStatus}`
        );
      }
      case 'bearing': {
        const bearing = bearings.find((possibleBearing) => possibleBearing.guid === guid);
        if (!bearing) return undefined;
        const { customModels, standardModels } = this.props.getHangerModelsAssociatedWithBearing(bearing.guid);
        const bearingHardware = [...customModels, ...standardModels].toSorted(naturalSort);
        const bearingHardwareText = bearingHardware.length === 0 ? '' : ` (${bearingHardware.join(', ')})`;
        const bearingsByJoint = analysisSet?.bearingsByJoint;
        let reactionsText = '';
        if (bearingsByJoint) {
          const associatedJoints = Object.entries(bearingsByJoint)
            .filter(([_jointGuid, bearingGuid]) => bearingGuid === bearing.guid)
            .map(([jointGuid, _bearingGuid]) =>
              componentDesign.analogModel?.analogJoints.find((joint) => joint.guid === jointGuid),
            )
            .filter(notNullish);
          const firstTwoJointsText = associatedJoints
            .slice(0, 2)
            .map((joint) => {
              const reactions = analysisSet.maximumBearingReactions.find(
                (reaction) => reaction.jointName === joint.name,
              );
              if (!reactions) return undefined;
              return `Joint ${joint.name}
                Up: ${formatBearingReaction(reactions.upForce)}
                Down: ${formatBearingReaction(reactions.downForce)}
                Horizontal: ${formatBearingReaction(reactions.horizontalForce)}
                ${getObjectCheckFailureSummaryString(joint.guid, analysisSet)}`;
            })
            .join('\n');
          const continuationText = associatedJoints.length > 2 ? `${associatedJoints.length - 2} more` : '';
          reactionsText = `${firstTwoJointsText}
            ${continuationText}`;
        }
        return `${bearing.name}
          ${startCase(bearing.bearingType)}${bearingHardwareText}
          ${reactionsText}`;
      }
      case 'analogJoint':
        const joint = componentDesign.analogModel?.analogJoints.find((joint) => joint.guid === guid);
        return (
          joint &&
          `Joint ${joint.name}
          ${formatBoundaryType(joint.boundaryType)}
          ${objectCheckFailureSummaryString}`
        );
      case 'analogLink':
        const link = componentDesign.analogModel?.analogLinks.find((link) => link.guid === guid);
        const memberSummary = analysisSet?.memberStressSummaries.find((summary) => summary.linkGuid === link?.guid);
        const maxMoment =
          memberSummary &&
          maxBy(
            [memberSummary.adjustedIEndMoment, memberSummary.adjustedMidPanelMoment, memberSummary.adjustedJEndMoment],
            ({ value }) => Math.abs(value),
          );
        return (
          link &&
          `Link ${link.name}
            ${distanceFormatter(link.length)}
            I Fixity: ${link.iFixity.momentRatio}${doesStiffnessLimitFactorApply(link.iFixity) ? '*' : ''}
            J Fixity: ${link.jFixity.momentRatio}${doesStiffnessLimitFactorApply(link.jFixity) ? '*' : ''}
            ${
              memberSummary
                ? `
              (Load Case)
              CSI: ${memberSummary.csi.value} (${memberSummary.csi.loadCaseIndex})
              SSI: ${memberSummary.ssi.value} (${memberSummary.ssi.loadCaseIndex})
              ${
                memberSummary.compression && memberSummary.tension
                  ? ''
                  : `Mid Panel Axial: ${formatForce(memberSummary.midPanelAxial.value)} (${
                      memberSummary.midPanelAxial.loadCaseIndex
                    })`
              }
              ${
                memberSummary.compression
                  ? `Compression: ${formatForce(memberSummary.compression.value)} (${
                      memberSummary.compression.loadCaseIndex
                    })`
                  : ''
              }
              ${
                memberSummary.tension
                  ? `Tension: ${formatForce(memberSummary.tension.value)} (${memberSummary.tension.loadCaseIndex})`
                  : ''
              }
              I End Moment / K_m: ${formatMoment(memberSummary.adjustedIEndMoment.value)} (${
                memberSummary.adjustedIEndMoment.loadCaseIndex
              })
              J End Moment / K_m: ${formatMoment(memberSummary.adjustedJEndMoment.value)} (${
                memberSummary.adjustedJEndMoment.loadCaseIndex
              })
              Max Moment / K_m: ${formatMoment(maxMoment!.value)} (${maxMoment!.loadCaseIndex})
              Shear: ${formatForce(memberSummary.shear.value)} (${memberSummary.shear.loadCaseIndex})
              Max Bracing Spacing: ${distanceFormatter(memberSummary.purlinSpacing)}
              `
                : ``
            }
            ${objectCheckFailureSummaryString}`
        );
      case 'hanger':
        const hanger = componentDesign.component.hangers.find((hanger) => hanger.guid === guid);
        if (!hanger) return undefined;
        const name = connectorDisplayName(hanger.connector);
        const locked = componentDesign.hangersLocked || componentDesign.lockedHangers.includes(hanger.guid);
        const lockedStatus = locked ? 'Locked' : 'Unlocked';
        const modelNotShown = lookupHangerMeshKey(hanger.connector) == null;
        return `${name}
          ${lockedStatus}
          ${modelNotShown ? 'Model not shown' : ''}`;
      case 'panel': {
        const panel = componentDesign.analogModel?.panels.find((panel) => panel.guid === guid);
        if (!panel) return undefined;
        const length = getAnalogPanelLength(panel, componentDesign.analogModel!);
        return `Panel ${panel.name}
          ${distanceFormatter(length)}
          ${objectCheckFailureSummaryString}`;
      }
      case 'referenceLine': {
        const line = findLine(guid, componentDesign.component);
        if (!line) return undefined;

        return line.lineType === 'Member' ? 'Member Line' : `${line.name} ${startCase(line.lineType)} Line`;
      }

      case 'webJointSegment':
        return 'Profile line—right click to add or remove panels';
      case 'webSegment':
        return 'Web Segment—right click or select to delete';
      default:
        return null;
    }
  };

  render() {
    const threeTooltipText = this.getHoveredItemText();
    if (threeTooltipText) {
      return <TooltipTextContent text={threeTooltipText} />;
    }

    return null;
  }
}

function doesStiffnessLimitFactorApply(fixity: Fixity) {
  return fixity.stiffnessLimitFactor != null && fixity.momentRatio !== 0;
}

const mapStateToProps = (state: RootState) => ({
  componentDesign: getActiveComponentDesign(state),
  analysisSet: getActiveAnalysisSet(state),
  hoveredItem: state.viewport.hoveredItem,
  memberLabelOptions: state.visualOptions.memberLabelOptions,
  formatNominalLumberDimensions: createNominalLumberDimensionFormatter(state),
  distanceFormatter: getDistanceFormatterSelector(state),
  plateTypeFormatter: getPlateTypeFormatter(state),
  getHangerModelsAssociatedWithBearing: getHangerModelsAssociatedWithBearing(state),
});

export default connect(mapStateToProps)(DesignModeTooltip);
